/**
 * BESTIX v5.0 — EventsPage
 * Event listing page with filters - powered by Bespokian Events API
 */

import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SlidersHorizontal } from 'lucide-react';
import { EventGrid } from '@/components/events/EventGrid';
import { EventFilters } from '@/components/events/EventFilters';
import { CategoryHero } from '@/components/hero/Hero';
import { GuaranteeBanner } from '@/components/ui/GuaranteeBanner';
import { useEvents, useCategories, type SearchEventsParams } from '@/queries/events.queries';
import { categoryHeroImages, leagueHeroImages, defaultHero } from '@/config/hero-images';
import type { Event, Category } from '@/lib/api';

const categoryHeadlines: Record<string, { headline: string; subheadline: string }> = {
  concerts: { headline: 'Live Music,', subheadline: 'Unforgettable Moments' },
  sports: { headline: 'Premium Seats,', subheadline: 'Legendary Games' },
  theater: { headline: 'The World\'s Greatest Stages,', subheadline: 'Reserved for You' },
  nba: { headline: 'NBA,', subheadline: 'Courtside & Beyond' },
  soccer: { headline: 'Soccer,', subheadline: 'Every Match Matters' },
  tennis: { headline: 'Tennis,', subheadline: 'Grand Slam Moments' },
  festivals: { headline: 'Exclusive Access,', subheadline: 'Extraordinary Gatherings' },
};

const leagueHeadlines: Record<string, { headline: string; subheadline: string }> = {
  nba: { headline: 'NBA,', subheadline: 'Courtside & Beyond' },
  mlb: { headline: 'MLB,', subheadline: 'America\'s Pastime' },
  nfl: { headline: 'NFL,', subheadline: 'Gridiron Glory' },
  soccer: { headline: 'Soccer,', subheadline: 'Every Match Matters' },
  tennis: { headline: 'Tennis,', subheadline: 'Grand Slam Moments' },
  golf: { headline: 'Golf,', subheadline: 'The Majors Await' },
};

function SortDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-[var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[var(--color-gold)]"
    >
      <option value="date">Sort by Date</option>
      <option value="price">Sort by Price</option>
      <option value="name">Sort by Name</option>
    </select>
  );
}

// Fallback images by category
const categoryImages: Record<string, string> = {
  concerts: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&q=80',
  sports: 'https://images.unsplash.com/photo-1461896836934-28e4b8f0e7ca?w=600&q=80',
  theater: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=600&q=80',
  festivals: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&q=80',
  default: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=600&q=80',
};

function getCategoryImage(category?: string): string {
  if (!category) return categoryImages.default;
  const lower = category.toLowerCase();
  if (lower.includes('concert') || lower.includes('music')) return categoryImages.concerts;
  if (lower.includes('sport') || lower.includes('nba') || lower.includes('nfl') || lower.includes('mlb')) return categoryImages.sports;
  if (lower.includes('theater') || lower.includes('broadway')) return categoryImages.theater;
  if (lower.includes('festival')) return categoryImages.festivals;
  return categoryImages.default;
}

// Parse date and time strings into a displayable format
function parseEventDateTime(dateStr: string, timeStr: string): string {
  try {
    // Parse the date part (YYYY-MM-DD)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    // Format the date
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    
    return `${formattedDate} · ${timeStr}`;
  } catch {
    return `${dateStr} · ${timeStr}`;
  }
}

// Transform API event to EventCard props
function transformEventToCardProps(event: Event) {
  return {
    id: event.id,
    title: event.name,
    subtitle: event.performers?.[0]?.name !== event.name ? event.performers?.[0]?.name : undefined,
    venue: event.venue.name,
    location: [event.venue.city, event.venue.state].filter(Boolean).join(', ') || 'Various Locations',
    dateTime: parseEventDateTime(event.date, event.time),
    priceFrom: event.priceRange ? Math.round(event.priceRange.min * 100) : 0,
    ticketsAvailable: event.ticketsAvailable ? 100 : 0,
    heatLevel: (event.ticketsAvailable ? 4 : 2) as 1 | 2 | 3 | 4 | 5,
    imageUrl: event.imageUrl || getCategoryImage(event.category),
    category: (event.category?.toUpperCase() || 'EVENT') as 'CONCERTS' | 'SPORTS' | 'THEATER' | 'FESTIVALS',
  };
}

export default function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const category = searchParams.get('category') || undefined;
  const league = searchParams.get('league') || undefined;
  const search = searchParams.get('q') || searchParams.get('search') || undefined;
  const page = parseInt(searchParams.get('page') || '1', 10);
  const performerId = searchParams.get('performer_id') ? parseInt(searchParams.get('performer_id')!, 10) : undefined;
  const venueId = searchParams.get('venue_id') ? parseInt(searchParams.get('venue_id')!, 10) : undefined;

  // Fetch categories from API
  const { data: categoriesData } = useCategories();

  // Transform categories to filter options (uses path for filtering, nested children)
  const categoryFilters = useMemo(() => {
    if (!categoriesData) {
      // Return empty while loading - the component will show a loading state
      return [];
    }
    
    // Transform category - use path as value for API filtering
    const transformCategory = (cat: Category): { value: string; label: string; count: number; children?: { value: string; label: string; count: number }[] } => {
      const result: { value: string; label: string; count: number; children?: { value: string; label: string; count: number }[] } = {
        value: cat.path, // Use path for API filtering
        label: cat.name,
        count: cat.eventCount || 0,
      };
      
      if (cat.children && cat.children.length > 0) {
        result.children = cat.children.map(child => ({
          value: child.path, // Use path for API filtering
          label: child.name,
          count: child.eventCount || 0,
        }));
      }
      
      return result;
    };
    
    return categoriesData.map(transformCategory);
  }, [categoriesData]);

  // Build API params
  const apiParams: SearchEventsParams = useMemo(() => ({
    q: search,
    category: category,
    performer_id: performerId,
    venue_id: venueId,
    page,
    per_page: 24,
  }), [search, category, performerId, venueId, page]);

  // Fetch events from API
  const { data, isLoading, error } = useEvents(apiParams);

  const events = useMemo(() => {
    if (!data?.events) return [];
    return data.events.map(transformEventToCardProps);
  }, [data?.events]);

  const handleSortChange = (newSort: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('sortBy', newSort);
    setSearchParams(params);
  };

  const primaryCategoryPath = category?.split(',')[0];
  
  // Look up category name from path
  const getCategoryName = (path: string | undefined): string | null => {
    if (!path || !categoriesData) return null;
    
    // Search in parent categories and their children
    for (const cat of categoriesData) {
      if (cat.path === path) return cat.name;
      if (cat.children) {
        for (const child of cat.children) {
          if (child.path === path) return child.name;
        }
      }
    }
    return null;
  };
  
  const categoryName = getCategoryName(primaryCategoryPath);
  const categorySlug = categoryName?.toLowerCase().replace(/\s+/g, '-');
  
  const displayName = league 
    ? league.toUpperCase() 
    : categoryName 
    ? categoryName
    : null;
  
  const title = displayName 
    ? `${displayName} Events` 
    : search 
    ? `Search: ${search}` 
    : 'All Events';

  const heroImage = league && leagueHeroImages[league]
    ? leagueHeroImages[league]
    : categorySlug 
    ? categoryHeroImages[categorySlug] || defaultHero
    : defaultHero;

  const heroContent = league && leagueHeadlines[league]
    ? leagueHeadlines[league]
    : categorySlug 
    ? categoryHeadlines[categorySlug] 
    : undefined;

  return (
    <>
      <Helmet>
        <title>{title} | BESTIX</title>
        <meta name="description" content={`Browse ${title.toLowerCase()} and find premium tickets on BESTIX.`} />
      </Helmet>

      <CategoryHero
        title={title}
        eventCount={data?.totalCount || events.length}
        backgroundImage={heroImage}
        headline={heroContent?.headline}
        subheadline={heroContent?.subheadline}
      />

      <GuaranteeBanner variant="compact" />

      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex gap-8">
            {/* Desktop Sidebar Filters */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-md overflow-hidden">
                <EventFilters categories={categoryFilters} />
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-[var(--color-navy)] hover:bg-gray-50"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                </button>

                <div className="hidden lg:block text-sm text-gray-600">
                  {isLoading ? 'Loading...' : `Showing ${events.length} events`}
                  {data?.totalCount ? ` of ${data.totalCount}` : null}
                </div>

                <SortDropdown value={searchParams.get('sortBy') || 'date'} onChange={handleSortChange} />
              </div>

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-700">Failed to load events. Please try again.</p>
                </div>
              )}

              {/* Event Grid */}
              {isLoading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="animate-pulse rounded-xl bg-gray-200 h-80" />
                  ))}
                </div>
              ) : events.length > 0 ? (
                <EventGrid events={events} columns={3} />
              ) : (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">🎭</div>
                  <h3 className="text-xl font-semibold text-[var(--color-navy)] mb-2">No events found</h3>
                  <p className="text-gray-600">Try adjusting your filters or search terms</p>
                </div>
              )}

              {/* Pagination */}
              {data && (data.totalCount ?? 0) > 24 && (
                <div className="flex justify-center gap-2 mt-8">
                  {page > 1 && (
                    <Link
                      to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), page: String(page - 1) })}`}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Previous
                    </Link>
                  )}
                  <span className="px-4 py-2 text-gray-600">
                    Page {page} of {Math.ceil((data.totalCount ?? 0) / 24)}
                  </span>
                  {page * 24 < (data.totalCount ?? 0) && (
                    <Link
                      to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), page: String(page + 1) })}`}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Next
                    </Link>
                  )}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-full max-w-sm bg-white shadow-xl lg:hidden animate-slide-in-right">
            <EventFilters
              isMobile
              onClose={() => setMobileFiltersOpen(false)}
              categories={categoryFilters}
            />
          </div>
        </>
      )}
    </>
  );
}
